import { useEffect, useMemo, useRef, useState } from "react";
import { Country } from "country-state-city";
import type { ICountry } from "country-state-city";
import {
  formatIncompletePhoneNumber,
  getCountryCallingCode,
  isSupportedCountry,
  isValidPhoneNumber,
} from "libphonenumber-js";
import type { CountryCode } from "libphonenumber-js";
import { ChevronDown, Search } from "lucide-react";

const IPINFO_LITE_TOKEN = "f5459ea5fbe2bb";

const IPINFO_LITE_URL = `https://api.ipinfo.io/lite/me?token=${encodeURIComponent(
  IPINFO_LITE_TOKEN
)}`;

type IpInfoLiteResponse = {
  country_code?: string;
};

const fetchIpInfoCountryCode = async () => {
  const response = await fetch(IPINFO_LITE_URL);

  if (!response.ok) {
    throw new Error("Unable to detect country from IPInfo.");
  }

  const data = (await response.json()) as IpInfoLiteResponse;

  return data.country_code?.trim().toUpperCase() || "";
};

type PhoneCountry = {
  isoCode: CountryCode;
  name: string;
  flag: string;
  callingCode: string;
};

type InternationalPhoneInputProps = {
  name?: string;
  required?: boolean;
  onAutoDetectCountry?: (country: PhoneCountry) => void;
};

const DEFAULT_COUNTRY_CODE = "CY";

function toPhoneCountry(country: ICountry): PhoneCountry | null {
  const isoCode = country.isoCode.trim().toUpperCase();

  if (!isSupportedCountry(isoCode)) {
    return null;
  }

  return {
    isoCode,
    name: country.name,
    flag: country.flag,
    callingCode: `+${getCountryCallingCode(isoCode)}`,
  };
}

function getValidationError(value: string, country: PhoneCountry) {
  if (!value.trim()) {
    return "Phone number is required.";
  }

  if (!isValidPhoneNumber(value, country.isoCode)) {
    return `Enter a valid phone number for ${country.name}.`;
  }

  return "";
}

export default function InternationalPhoneInput({
  name = "phone",
  required = true,
  onAutoDetectCountry,
}: InternationalPhoneInputProps) {
  const countries = useMemo(() => {
    return Country.getAllCountries()
      .map(toPhoneCountry)
      .filter((country): country is PhoneCountry => Boolean(country))
      .sort((first, second) => first.name.localeCompare(second.name));
  }, []);

  const defaultCountry =
    countries.find((country) => country.isoCode === DEFAULT_COUNTRY_CODE) ||
    countries[0] || {
      isoCode: DEFAULT_COUNTRY_CODE,
      name: "Cyprus",
      flag: "CY",
      callingCode: "+357",
    };

  const [selectedCountry, setSelectedCountry] =
    useState<PhoneCountry>(defaultCountry);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [isTouched, setIsTouched] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let isMounted = true;

    fetchIpInfoCountryCode()
      .then((countryCode) => {
        if (!isMounted || !countryCode) {
          return;
        }

        const detectedCountry = countries.find(
          (country) => country.isoCode === countryCode
        );

        if (detectedCountry) {
          setSelectedCountry(detectedCountry);
          onAutoDetectCountry?.(detectedCountry);
        }
      })
      .catch(() => undefined);

    return () => {
      isMounted = false;
    };
  }, [countries, onAutoDetectCountry]);

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
    };
  }, []);

  const filteredCountries = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    if (!normalizedSearch) {
      return countries;
    }

    return countries.filter((country) => {
      return (
        country.name.toLowerCase().includes(normalizedSearch) ||
        country.isoCode.toLowerCase().includes(normalizedSearch) ||
        country.callingCode.includes(normalizedSearch)
      );
    });
  }, [countries, searchTerm]);

  const validationError =
    isTouched || phoneNumber ? getValidationError(phoneNumber, selectedCountry) : "";
  const fullPhoneNumber = phoneNumber.trim()
    ? `${selectedCountry.callingCode}${phoneNumber.replace(/\s/g, "")}`
    : "";

  return (
    <div className="form-group full phone-field">
      <label htmlFor={name}>
        Phone <span>*</span>
      </label>
      <div
        className={`phone-input-row ${validationError ? "has-error" : ""}`}
        ref={dropdownRef}
      >
        <div className="phone-country-picker">
          <button
            type="button"
            className="phone-country-trigger"
            onClick={() => setIsOpen((current) => !current)}
            aria-label="Select country code"
            aria-expanded={isOpen}
          >
            <img
              className="phone-country-flag"
              src={`https://flagcdn.com/w40/${selectedCountry.isoCode.toLowerCase()}.png`}
              alt=""
              aria-hidden="true"
            />
            <span className="phone-country-prefix">
              {selectedCountry.callingCode}
            </span>
            <ChevronDown size={16} aria-hidden="true" />
          </button>

          {isOpen && (
            <div className="phone-country-menu">
              <div className="phone-country-search">
                <Search size={16} aria-hidden="true" />
                <input
                  type="search"
                  placeholder="Search country"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  autoFocus
                />
              </div>
              <div className="phone-country-list">
                {filteredCountries.map((country) => (
                  <button
                    type="button"
                    className="phone-country-option"
                    key={country.isoCode}
                    onClick={() => {
                      setSelectedCountry(country);
                      setSearchTerm("");
                      setIsOpen(false);
                    }}
                  >
                    <img
                      className="phone-country-flag"
                      src={`https://flagcdn.com/w40/${country.isoCode.toLowerCase()}.png`}
                      alt=""
                      aria-hidden="true"
                    />
                    <span className="phone-country-name">{country.name}</span>
                    <span className="phone-country-prefix">
                      {country.callingCode}
                    </span>
                  </button>
                ))}
                {!filteredCountries.length && (
                  <div className="phone-country-empty">No countries found</div>
                )}
              </div>
            </div>
          )}
        </div>

        <input
          id={name}
          name={name}
          type="tel"
          placeholder="70 123 45 67"
          value={phoneNumber}
          required={required}
          aria-invalid={Boolean(validationError)}
          aria-describedby={validationError ? `${name}-error` : undefined}
          onBlur={() => setIsTouched(true)}
          onChange={(event) => {
            setPhoneNumber(
              formatIncompletePhoneNumber(event.target.value, selectedCountry.isoCode)
            );
          }}
        />
      </div>
      <input type="hidden" name={`${name}_country`} value={selectedCountry.isoCode} />
      <input type="hidden" name={`${name}_full`} value={fullPhoneNumber} />
      {validationError && (
        <p className="phone-validation-error" id={`${name}-error`}>
          {validationError}
        </p>
      )}
    </div>
  );
}
